import styled from 'styled-components';
import { TextField, Autocomplete } from '@mui/material';

const BackButton = styled.button`
  background: none;
  border: none;
  font-size: 18px;
  cursor: pointer;
  margin-bottom: 20px;
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 15px;
  color: black;
  border-radius: 8px;
  transition: all 0.3s ease;
  font-weight: 500;

  &:hover {
    background-color: rgba(0, 0, 0, 0.1);
    transform: translateX(-2px);
  }
`;

const Title = styled.h1`
  width: 352px;
  height: 55px;
  position: absolute;
  top: -5px;
  left: 50%;
  transform: translateX(-50%);
  
  /* Typography settings */
  font-family: 'Ubuntu', sans-serif;
  font-weight: 600;
  font-size: 40px;
  line-height: 45.96px;
  text-align: center;
  color: black;
  
  /* Layout */
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  
  @media (max-width: 768px) {
    position: static;
    width: 100%;
    height: auto;
    margin: 15px 0;
    transform: none;
  }

  @media (max-width: 640px) {
    position: static;
    width: 100%;
    transform: none;
    font-size: 32px;
    margin: 20px 0;
    height: auto;
  }
`;

const FormContainer = styled.div`
  display: flex;
  gap: 40px;
  margin: 0 auto;
  max-width: 900px;
  background: rgba(255, 255, 255, 0.8);
  border-radius: 20px;
  padding: 30px;
  box-shadow: 0 8px 32px rgba(107, 137, 255, 0.1);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(107, 137, 255, 0.1);

  @media (max-width: 640px) {
    flex-direction: column;
    align-items: center;
    width: 100%;
    padding: 20px 16px;
    margin: 0 auto;
    gap: 20px;
  }
`;

const ImageSection = styled.div`
  flex: 0 0 221px;
  margin-top: 1px;
  display: flex;
  flex-direction: column;
  gap: 20px;

  @media (max-width: 640px) {
    width: 100%;
    margin: 0;
    align-items: center;
  }
`;

const FormSection = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 17px;

  @media (max-width: 640px) {
    width: 100%;
    gap: 15px;
  }
`;

const BookImage = styled.img`
  width: 180px;
  height: 240px;
  object-fit: cover;
  border-radius: 15px;
  border: 1px solid rgba(107, 137, 255, 0.2);
  margin-left: 40px;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
  transition: all 0.3s ease;

  &:hover {
    transform: scale(1.05);
    box-shadow: 0 12px 35px rgba(0, 0, 0, 0.2);
  }

  @media (max-width: 640px) {
    margin: 0;
    width: 160px;
    height: 213px;
  }
`;

const PhotoUploadSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-left: 70px;

  @media (max-width: 640px) {
    width: 100%;
    margin: 0;
    align-items: center;
  }
`;

const PhotoLabel = styled.p`
  margin: 0;
  font-weight: 500;
  color: #6B89FF;
  font-size: 14px;
`;

const PhotoUploadButton = styled.button`
  width: 100px;
  height: 100px;
  background: linear-gradient(135deg, #6B89FF 0%, #5A75E6 100%);
  border: none;
  border-radius: 15px;
  color: white;
  font-size: 32px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(107, 137, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #5A75E6 0%, #4A65D6 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(107, 137, 255, 0.4);
  }
`;

const PublishButton = styled.button`
  width: 120px;
  height: 45px;
  background: linear-gradient(135deg, #6B89FF 0%, #5A75E6 100%);
  color: white;
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(107, 137, 255, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  
  /* Typography settings */
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 22.98px;
  text-align: center;
  
  &:hover {
    background: linear-gradient(135deg, #5A75E6 0%, #4A65D6 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(107, 137, 255, 0.4);
  }
`;

const BaseTextField = styled(TextField)`
  .MuiInputBase-root {
    height: 45px;
    font-size: 14px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(107, 137, 255, 0.2);
    transition: all 0.3s ease;
    
    &:hover {
      border-color: rgba(107, 137, 255, 0.4);
      box-shadow: 0 4px 15px rgba(107, 137, 255, 0.1);
    }
    
    &.Mui-focused {
      border-color: #6B89FF;
      box-shadow: 0 4px 15px rgba(107, 137, 255, 0.2);
    }
  }

  .MuiInputBase-input {
    height: 45px;
    display: flex;
    align-items: center;
    padding: 0 15px !important;
    position: static;
    font-size: 14px;
    text-align: left;
  }

  .MuiInputLabel-root {
    text-align: left;
    left: 0px;
    top: -10px;
    color: black;
    font-weight: 500;
  }

  .MuiInputLabel-shrink {
    transform-origin: left;
    color: black;
  }

  .MuiFormHelperText-root {
    position: absolute;
    bottom: -20px;
    margin: 0;
    font-size: 12px;
    height: 0;
  }

  margin-bottom: 0 !important;
`;

const TitleField = styled(BaseTextField)`
  width: 300px !important;

  @media (max-width: 640px) {
    width: 100% !important;
  }

  && {
    .MuiFormHelperText-root {
      margin: 0;
      height: 0;
    }
  }
`;

const EditionField = styled(BaseTextField)`
  width: 80px !important;

  @media (max-width: 640px) {
    width: 100% !important;
  }
`;

const AuthorField = styled(BaseTextField)`
  width: 300px;

  @media (max-width: 640px) {
    width: 100% !important;
  }

  && {
    .MuiFormHelperText-root {
      margin: 0;
      height: 0;
    }
  }
`;

const IsbnField = styled(BaseTextField)`
  width: 200px;
`;

const PriceField = styled(BaseTextField)`
  width: 100px;

  && {
    .MuiFormHelperText-root {
      margin: 0;
      height: 0;
    }
  }
`;

const DescriptionField = styled(BaseTextField)`
  width: 300px !important;

  .MuiInputBase-root {
    height: auto;
    min-height: 120px;
  }

  .MuiInputBase-input {
    height: auto !important;
    padding: 0 12px !important;
    align-items: flex-start;
    position: relative;
  }

  textarea {
    padding-top: 0 !important;
    margin-top: 0 !important;
    line-height: 1.5;
    position: relative;
    top: -10px;
    left: -15px;
  }

  .MuiInputLabel-root {
    text-align: left;
    left: 0px;
    top: -10px;
  }

  @media (max-width: 640px) {
    width: 100% !important;
  }
`;

const InputRow = styled.div`
  display: flex;
  gap: 20px;

  @media (max-width: 640px) {
    flex-direction: column;
    width: 100%;
    gap: 15px;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 20px;

  @media (max-width: 640px) {
    width: 100%;
    justify-content: center;
    margin-top: 20px;
  }
`;

const ClearButton = styled.button`
  width: 120px;
  height: 45px;
  background: linear-gradient(135deg, #808080 0%, #666666 100%);
  color: white;
  border: none;
  border-radius: 12px;
  box-shadow: 0 4px 15px rgba(128, 128, 128, 0.3);
  cursor: pointer;
  transition: all 0.3s ease;
  
  /* Typography settings */
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 18px;
  line-height: 22.98px;
  text-align: center;
  
  &:hover {
    background: linear-gradient(135deg, #666666 0%, #555555 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(128, 128, 128, 0.4);
  }
`;

const SignInModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(5px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: rgba(255, 255, 255, 0.95);
  padding: 30px;
  border-radius: 20px;
  width: 500px;
  box-shadow: 0 8px 32px rgba(107, 137, 255, 0.2);
  backdrop-filter: blur(10px);
  border: 1px solid rgba(107, 137, 255, 0.1);

  @media (max-width: 640px) {
    width: 90%;
    margin: 0 16px;
  }
`;

const ModalTitle = styled.h2`
  margin-bottom: 10px;
`;

const ModalText = styled.p`
  margin-bottom: 20px;
`;

const ErrorMessage = styled.div`
  color: #d32f2f;
  background: linear-gradient(135deg, #fde8e8 0%, #fce4e4 100%);
  padding: 8px 15px;
  border-radius: 12px;
  margin-bottom: -14px;
  margin-top: -29px;
  font-size: 14px;
  width: 400px;
  min-height: 8px;
  text-align: left;
  border: 1px solid #ffa4a4;
  line-height: 1.4;
  box-shadow: 0 4px 15px rgba(211, 47, 47, 0.1);
  
  @media (max-width: 640px) {
    width: 100%;
    margin-top: 10px;
    margin-bottom: 10px;
  }
`;

const ModalButtonContainer = styled.div`
  display: flex;
  gap: 20px;
  margin-top: 20px;
`;

const CancelButton = styled.button`
  width: 120px;
  height: 45px;
  background: linear-gradient(135deg, #6B89FF 0%, #5A75E6 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(107, 137, 255, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #5A75E6 0%, #4A65D6 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(107, 137, 255, 0.4);
  }
`;

const ConfirmButton = styled.button`
  width: 120px;
  height: 45px;
  background: linear-gradient(135deg, #808080 0%, #666666 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-family: 'Ubuntu', sans-serif;
  font-weight: 700;
  font-size: 16px;
  cursor: pointer;
  box-shadow: 0 4px 15px rgba(128, 128, 128, 0.3);
  transition: all 0.3s ease;

  &:hover {
    background: linear-gradient(135deg, #666666 0%, #555555 100%);
    transform: translateY(-2px);
    box-shadow: 0 8px 25px rgba(128, 128, 128, 0.4);
  }
`;

const StyledAutocomplete = styled(Autocomplete)`
  width: 400px;
  
  .MuiAutocomplete-input {
    padding: 10px 12px !important;
  }
  
  .MuiInputBase-root {
    height: 45px;
    border-radius: 12px;
    background: rgba(255, 255, 255, 0.9);
    backdrop-filter: blur(10px);
    border: 1px solid rgba(107, 137, 255, 0.2);
    transition: all 0.3s ease;
    
    &:hover {
      border-color: rgba(107, 137, 255, 0.4);
      box-shadow: 0 4px 15px rgba(107, 137, 255, 0.1);
    }
    
    &.Mui-focused {
      border-color: #6B89FF;
      box-shadow: 0 4px 15px rgba(107, 137, 255, 0.2);
    }
  }
  
  .MuiAutocomplete-input {
    height: 35px !important;
    padding: 0 12px !important;
  }
  
  .MuiAutocomplete-listbox {
    max-height: 200px;
    overflow-y: auto;
    border-radius: 12px;
    box-shadow: 0 8px 25px rgba(0, 0, 0, 0.15);
    border: 1px solid rgba(107, 137, 255, 0.1);
  }
  
  @media (max-width: 640px) {
    width: 100%;
    .MuiInputBase-root {
      width: 100%;
    }
  }
`;

export {
  BackButton,
  Title,
  FormContainer,
  ImageSection,
  FormSection,
  BookImage,
  PhotoUploadSection,
  PhotoLabel,
  PhotoUploadButton,
  PublishButton,
  TitleField,
  EditionField,
  AuthorField,
  IsbnField,
  PriceField,
  DescriptionField,
  InputRow,
  ButtonContainer,
  ClearButton,
  SignInModal,
  ModalContent,
  ModalTitle,
  ModalText,
  ErrorMessage,
  ModalButtonContainer,
  CancelButton,
  ConfirmButton,
  StyledAutocomplete
};